"use client";

import { Phone, PhoneIncoming } from "lucide-react";

interface CallBlockProps {
  entryType: "call_outbound" | "call_inbound";
  metadata: Record<string, unknown> | null;
  body: string | null;
  createdAt: string;
  userName: string | null;
}

export function CallBlock({ entryType, metadata, body, createdAt, userName }: CallBlockProps) {
  const isOutbound = entryType === "call_outbound";
  const Icon = isOutbound ? Phone : PhoneIncoming;
  const borderClass = isOutbound ? "border-x-4 border-green-500" : "border-l-4 border-l-blue-500";
  const iconBg = isOutbound ? "bg-green-100 text-green-600" : "bg-blue-100 text-blue-600";
  const duration = metadata?.duration as number | undefined;
  const outcome = metadata?.outcome as string | undefined;
  const recordingUrl = metadata?.recordingUrl as string | undefined;

  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
      <div className={`min-w-[85%] max-w-[85%] rounded-lg border border-gray-100 bg-white p-3 ${borderClass}`}>
        <div className="flex items-start gap-2">
          <div className={`rounded-full p-1.5 ${iconBg}`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-900">
                {isOutbound ? "Outbound Call" : "Inbound Call"}
              </p>
              <span className="text-[10px] text-gray-400">
                {new Date(createdAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            {userName && <p className="text-[10px] text-gray-500">by {userName}</p>}
            <div className="mt-1 flex items-center gap-2">
              {outcome && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 capitalize">
                  {outcome}
                </span>
              )}
              {duration != null && (
                <span className="text-[10px] text-gray-500">
                  {Math.floor(duration / 60)}:{String(duration % 60).padStart(2, "0")}
                </span>
              )}
            </div>
            {body && <p className="mt-1 text-xs text-gray-600 break-words">{body}</p>}
            {recordingUrl && (
              <audio controls className="mt-2 h-8 w-full" preload="none">
                <source src={recordingUrl} />
              </audio>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
